// Generador de HTML imprimible de la Historia Clínica (Formulario 020 MSP).
// Compartido entre crear/editar (previsualizar) y el listado (ver historia clínica)
// para que ambas vistas sean idénticas.

export interface HistoriaClinicaPrintContext {
  admission?: any;
  consultation_reason?: any;
  personal_history?: any;
  current_illness?: any;
  vital_signs?: any;
  treatment_plan?: any;
  professional_data?: any;
  diagnoses?: any[];
  evolutions?: any[];
  systems_review?: { items?: any[] };
  physical_exam?: { items?: any[] };
  order?: { order_number?: string; invoice_number?: string; branch_name?: string };
}

export interface HistoriaClinicaPrintOptions {
  // true => muestra el botón "Imprimir / Guardar PDF"; false => solo previsualización
  permitirImpresion: boolean;
}

export function buildHistoriaClinicaHtml(
  context: HistoriaClinicaPrintContext,
  options: HistoriaClinicaPrintOptions
): string {
  const permitirImpresion = options.permitirImpresion;
  const order = context.order || {};
  const admission = context.admission || {};
  const consultation = context.consultation_reason || {};
  const history = context.personal_history || {};
  const illness = context.current_illness || {};
  const vitalSigns = context.vital_signs || {};
  const treatment = context.treatment_plan || {};
  const professional = context.professional_data || {};

  const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
  const logoUrl = new URL(
    `${baseHref.replace(/\/$/, '')}/assets/img/brand/logocm.png`,
    window.location.origin
  ).toString();
  const printDate = formatPrintDate(new Date());

  const diagnoses = (context.diagnoses || []).filter((item) => hasAnyValue([item.description, item.cie10]));
  const evolutions = (context.evolutions || []).filter((item) => hasAnyValue([
    item.subjective,
    item.objective,
    item.assessment,
    item.plan
  ]));
  const systems = (context.systems_review?.items || []).filter((item) => hasAnyValue([item.description]) || item.status === 'CP');
  const regions = (context.physical_exam?.items || []).filter((item) => hasAnyValue([item.description]) || item.status === 'anormal');
  const secondaryReasons = (consultation.secondary_reasons || []).filter((item: any) => String(item || '').trim());

  const admissionRows = [
    previewRow('Establecimiento de Salud', admission.health_facility),
    previewRow('N° Historia Clínica / Cédula', admission.clinical_record_number),
    previewRow('Orden', order.order_number),
    previewRow('Factura', order.invoice_number || 'Sin factura'),
    previewRow('Sucursal', order.branch_name),
    previewRow('Apellidos', admission.last_names),
    previewRow('Nombres', admission.names),
    previewRow('F. Nacimiento', admission.birth_date),
    previewRow('Edad', admission.age),
    previewRow('Nacionalidad', admission.nationality),
    previewRow('Sexo', admission.sex),
    previewRow('Estado Civil', admission.marital_status),
    previewRow('Identidad de Género', admission.gender_identity),
    previewRow('Autoidentidad Étnica', admission.ethnicity === 'Otro' ? admission.ethnicity_other : admission.ethnicity),
    previewRow('Residencia', admission.residence),
    previewRow('Dirección', admission.address),
    previewRow('Teléfono', admission.phone),
    previewRow('Correo', admission.email),
    previewRow('Instrucción', admission.education),
    previewRow('Ocupación / Empresa', admission.occupation),
    previewRow('Sangre y Factor Rh', admission.blood_type),
    previewRow('Contacto Emergencia', admission.emergency_name),
    previewRow('Parentesco', admission.emergency_relationship),
    previewRow('Teléfono Emergencia', admission.emergency_phone)
  ].join('');
  const consultationRows = [
    previewRow('Motivo Principal', consultation.main_reason),
    previewRow('Tiempo de Evolución', consultation.evolution_time_unit),
    previewRow('Tiempo exacto', consultation.exact_time),
    previewRow('Clasificación de la Atención', consultation.attention_classification)
  ].join('');
  const historyRows = [
    previewRow('Enfermedades Clínicas', history.clinical_diseases),
    previewRow('Cirugías Previas', history.previous_surgeries),
    previewRow('Alergias', history.allergies),
    previewRow('Transfusiones', history.transfusions),
    previewRow('Tabaco', history.tobacco ? `Sí ${history.tobacco_amount || ''}` : ''),
    previewRow('Alcohol', history.alcohol ? 'Sí' : ''),
    previewRow('Drogas', history.drugs ? 'Sí' : ''),
    previewRow('Actividad Física', history.physical_activity),
    previewRow('Antecedentes Familiares', history.family_history)
  ].join('');
  const illnessRows = [
    previewRow('Inicio y Evolución', illness.start_evolution),
    previewRow('Estado Actual y Características', illness.current_state),
    previewRow('Intensidad del Dolor EVA 1-10', illness.pain_intensity),
    previewRow('Síntomas Asociados / Trat. Previos', illness.associated_symptoms)
  ].join('');
  const vitalSignsRows = [
    previewRow('Observación general', vitalSigns.notes),
    previewRow('Presión Arterial', vitalSigns.blood_pressure ? `${vitalSigns.blood_pressure} mmHg` : ''),
    previewRow('Frecuencia Cardíaca', vitalSigns.heart_rate ? `${vitalSigns.heart_rate} lpm` : ''),
    previewRow('Frecuencia Respiratoria', vitalSigns.respiratory_rate ? `${vitalSigns.respiratory_rate} rpm` : ''),
    previewRow('Temperatura', vitalSigns.temperature ? `${vitalSigns.temperature} °C` : ''),
    previewRow('Saturación O2', vitalSigns.oxygen_saturation ? `${vitalSigns.oxygen_saturation} %` : ''),
    previewRow('Peso', vitalSigns.weight ? `${vitalSigns.weight} Kg` : ''),
    previewRow('Talla', vitalSigns.height ? `${vitalSigns.height} m` : ''),
    previewRow('IMC', vitalSigns.bmi ? `${vitalSigns.bmi} Kg/m²` : ''),
    previewRow('Perím. Cefálico', vitalSigns.head_circumference ? `${vitalSigns.head_circumference} cm` : ''),
    previewRow('Glicemia', vitalSigns.glucose ? `${vitalSigns.glucose} mg/dl` : '')
  ].join('');
  const treatmentRows = [
    previewRow('Plan Diagnóstico', treatment.diagnostic_plan),
    previewRow('Plan Terapéutico', treatment.pharmacological_prescription),
    previewRow('Dieta / Cuidados', treatment.diet_care),
    previewRow('Reposo Médico', treatment.medical_rest ? 'Sí' : ''),
    previewRow('Días', treatment.rest_days),
    previewRow('Desde', treatment.rest_from),
    previewRow('Hasta', treatment.rest_to),
    previewRow('Signos de Alarma', treatment.alarm_signs),
    previewRow('Próxima Cita', treatment.next_appointment)
  ].join('');
  const professionalRows = [
    previewRow('Fecha', professional.date),
    previewRow('Hora Inicio', professional.start_time),
    previewRow('Hora Fin', professional.end_time),
    previewRow('Médico Tratante', professional.doctor_name),
    previewRow('Código MSP / SENESCYT', professional.msp_code)
  ].join('');
  const gynecoRows = [
    previewRow('Menarquia', history.menarche),
    previewRow('FUM', history.fum),
    previewRow('Ciclos', history.cycles),
    previewRow('Vida Sexual', history.sexual_life),
    previewRow('Gestas', history.pregnancies),
    previewRow('Partos', history.births),
    previewRow('Abortos', history.abortions),
    previewRow('Cesáreas', history.cesareans),
    previewRow('Hijos Vivos', history.living_children),
    previewRow('PAP', history.pap)
  ].join('');
  const evolutionRows = evolutions.map((evolution, index) => `
    <div class="evolution-print-item">
      <h3>Evolución ${evolutions.length - index} · ${escapeHtml(formatDisplayDate(evolution.evolution_date || evolution.created_at))}</h3>
      <div class="grid">
        ${previewRow('Lo que refiere el paciente', evolution.subjective)}
        ${previewRow('Hallazgos del profesional', evolution.objective)}
        ${previewRow('Impresión / análisis clínico', evolution.assessment)}
        ${previewRow('Plan e indicaciones', evolution.plan)}
        ${previewRow('Registrado por', evolution.created_by_name)}
      </div>
    </div>
  `).join('');

  return `
    <html>
    <head>
      <title>${permitirImpresion ? 'Historia Clínica' : 'Previsualización Historia Clínica'}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: Arial, sans-serif; color: #111; margin: 0; background: #eef3fb; }
        .toolbar { position: sticky; top: 0; background: #0b2a66; color: #fff; padding: 12px 24px; display: flex; justify-content: space-between; align-items: center; z-index: 2; }
        .toolbar button { border: 0; background: #fff; color: #0b2a66; font-weight: 700; padding: 8px 14px; border-radius: 4px; cursor: pointer; }
        .page { width: 216mm; min-height: 279mm; margin: 14px auto; padding: 10mm 18mm 18mm; background: #fff; box-shadow: 0 8px 30px rgba(15, 23, 42, .18); }
        .company-header { display: flex; align-items: center; gap: 10px; margin: 0 0 18px; }
        .company-logo { width: 118px; max-height: 56px; object-fit: contain; display: block; }
        .company-data { font-size: 10px; line-height: 1.25; color: #111; }
        .company-data strong { font-weight: 800; }
        h1 { text-align: center; font-size: 19px; margin: 0; letter-spacing: .3px; }
        .subtitle { text-align: center; font-style: italic; margin: 5px 0 22px; font-size: 13px; }
        h2 { font-size: 15px; margin: 18px 0 10px; padding-bottom: 7px; border-bottom: 1px solid #111; }
        h3 { font-size: 13px; margin: 12px 0 7px; }
        .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 7px 18px; }
        .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 7px 18px; }
        .value { min-height: 18px; padding: 2px 0; }
        .label { font-weight: 700; }
        .muted { color: #4b5563; }
        table { width: 100%; border-collapse: collapse; margin-top: 6px; }
        th, td { border-bottom: 1px solid #777; padding: 5px; font-size: 12px; text-align: left; vertical-align: top; }
        th { font-weight: 700; }
        .footer { margin-top: 44px; text-align: center; font-weight: 700; }
        .evolution-print-item { border-left: 3px solid #111; margin: 10px 0; padding-left: 10px; }
        .signature { width: 280px; border-top: 1px solid #111; margin: 58px auto 4px; }
        @media print {
          body { background: #fff; }
          .toolbar { display: none; }
          .page { margin: 0; box-shadow: none; width: auto; min-height: auto; }
        }
      </style>
    </head>
    <body>
      <div class="toolbar">
        <strong>${permitirImpresion ? 'Historia Clínica Generada' : 'Previsualización de Historia Clínica'}</strong>
        ${permitirImpresion ? '<button onclick="window.print()">Imprimir / Guardar PDF</button>' : ''}
      </div>
      <div class="page">
        <div class="company-header">
          <img class="company-logo" src="${logoUrl}" alt="C&M">
          <div class="company-data">
            <div><strong>C & M IMAGENES MEDICINA Y SALUD S.A. CMSALUD S.A</strong></div>
            <div><strong>RUC:</strong> 0993199168001</div>
            <div><strong>COOP:</strong> CDLA. BOLIVARIANA Calle: 8VA NO Intersección: AV. DELTA Manzana: S/N TEL. 0993808042</div>
            <div><strong>FECHA DE IMPRESIÓN:</strong> ${printDate}</div>
          </div>
        </div>
        <h1>HISTORIA CLÍNICA DE CONSULTA EXTERNA - PRIMERA VEZ</h1>
        <div class="subtitle">Basado en el Formulario 020 del Ministerio de Salud Pública del Ecuador (MSP)</div>

        ${previewSection('REGISTRO DE ADMISIÓN Y DATOS DEL PACIENTE', `<div class="grid">${admissionRows}</div>`)}

        ${previewSection('MOTIVO DE CONSULTA', `<div class="grid">${consultationRows}</div>${secondaryReasons.length ? `<h3>Motivos Secundarios</h3><ol>${secondaryReasons.map((item: any) => `<li>${escapeHtml(item)}</li>`).join('')}</ol>` : ''}`)}

        ${previewSection('ANTECEDENTES PERSONALES Y FAMILIARES', `<div class="grid">${historyRows}</div>${gynecoRows ? `<h3>Gineco-Obstétricos</h3><div class="grid-3">${gynecoRows}</div>` : ''}`)}

        ${previewSection('ENFERMEDAD ACTUAL', `<div class="grid">${illnessRows}</div>`)}

        ${systems.length ? previewSection('REVISIÓN DE ÓRGANOS Y SISTEMAS', previewTable(['Sistema', 'SP/CP', 'Hallazgos'], systems.map((item) => [item.system, item.status, item.description]))) : ''}

        ${previewSection('SIGNOS VITALES Y ANTROPOMETRÍA', `<div class="grid">${vitalSignsRows}</div>`)}

        ${regions.length ? previewSection('EXAMEN FÍSICO REGIONAL', previewTable(['Región', 'Normal/Anormal', 'Hallazgos'], regions.map((item) => [item.region, item.status, item.description]))) : ''}

        ${diagnoses.length ? previewSection('DIAGNÓSTICO', previewTable(['N°', 'Descripción del Diagnóstico', 'CIE-10', 'Tipo'], diagnoses.map((item, index) => [index + 1, item.description, item.cie10, item.type]))) : ''}

        ${previewSection('PLAN DE MANEJO Y TRATAMIENTO', `<div class="grid">${treatmentRows}</div>`)}

        ${previewSection('DATOS DEL PROFESIONAL', `<div class="grid">${professionalRows}</div>`)}

        ${evolutionRows ? previewSection('EVOLUCIONES CLÍNICAS', evolutionRows) : ''}

        <div class="footer">
          <div class="signature"></div>
          Firma y Sello del Médico
        </div>
        <p class="subtitle">Documento confidencial elaborado bajo los lineamientos del Ministerio de Salud Pública (MSP) del Ecuador. Sujeto al secreto profesional.</p>
      </div>
    </body>
    </html>
  `;
}

function previewSection(title: string, content: string): string {
  if (!content.includes('class="label"') && !content.includes('<table') && !content.includes('<li>')) return '';
  return `<h2>${escapeHtml(title)}</h2>${content}`;
}

function previewRow(label: string, value: any): string {
  if (!hasAnyValue([value])) return '';
  return `<div><span class="label">${escapeHtml(label)}:</span> <span class="value">${escapeHtml(value)}</span></div>`;
}

function previewTable(headers: string[], rows: any[][]): string {
  const body = rows.length
    ? rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('')
    : `<tr><td colspan="${headers.length}" class="muted">Sin datos registrados</td></tr>`;

  return `<table><thead><tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('')}</tr></thead><tbody>${body}</tbody></table>`;
}

function escapeHtml(value: any): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatPrintDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatDisplayDate(value: any): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return formatPrintDate(date);
}

function hasAnyValue(values: any[]): boolean {
  return values.some((value) => value !== null && value !== undefined && String(value).trim() !== '');
}
